import { useEffect, useState } from 'react';
import { CheckCircle2, Sparkles, Wallet, TrendingUp, Clock, Crown, Receipt } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/ToastProvider';
import Modal from '../components/Modal';
import StatCard from '../components/StatCard';
import { getSubscriptionStatus, createOrder, verifyPayment, getInvoice, reportPaymentFailed } from '../api/paymentsApi';

function daysLeft(trialEndsAt) {
  const ms = new Date(trialEndsAt).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
}

function formatAmount(amountInPaise, currency) {
  if (amountInPaise == null) return '—';
  return `${currency === 'INR' ? '₹' : currency + ' '}${(amountInPaise / 100).toFixed(2)}`;
}

// Loaded on demand (only when the user actually opens checkout) rather than
// globally — Razorpay's SDK background-preloads localized checkout chunks
// continuously once present on a page, which was flooding the network tab
// on every route, not just this one.
let razorpayScriptPromise = null;
function loadRazorpayScript() {
  if (window.Razorpay) return Promise.resolve();
  if (razorpayScriptPromise) return razorpayScriptPromise;
  razorpayScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = resolve;
    script.onerror = () => { razorpayScriptPromise = null; reject(new Error('Failed to load Razorpay checkout script')); };
    document.body.appendChild(script);
  });
  return razorpayScriptPromise;
}

function InvoiceModal({ invoice, onClose }) {
  return (
    <Modal title="Invoice" icon={Receipt} onClose={onClose}>
      <div className="space-y-2.5 text-sm">
        <div className="flex justify-between"><span className="text-[#5f6368] dark:text-[#9aa0a6]">Invoice No.</span><span className="font-semibold text-[#202124] dark:text-[#e8eaed]">{invoice.invoiceNumber}</span></div>
        <div className="flex justify-between"><span className="text-[#5f6368] dark:text-[#9aa0a6]">Plan</span><span className="font-semibold text-[#202124] dark:text-[#e8eaed] capitalize">{invoice.plan}</span></div>
        <div className="flex justify-between"><span className="text-[#5f6368] dark:text-[#9aa0a6]">Amount</span><span className="font-semibold text-[#202124] dark:text-[#e8eaed]">{formatAmount(invoice.amount, invoice.currency)}</span></div>
        <div className="flex justify-between"><span className="text-[#5f6368] dark:text-[#9aa0a6]">Purchased On</span><span className="font-semibold text-[#202124] dark:text-[#e8eaed]">{invoice.purchasedAt ? new Date(invoice.purchasedAt).toLocaleString() : '—'}</span></div>
        <div className="flex justify-between gap-3"><span className="text-[#5f6368] dark:text-[#9aa0a6] shrink-0">Billed To</span><span className="font-semibold text-[#202124] dark:text-[#e8eaed] text-right">{invoice.billedTo?.name || '—'}<br /><span className="font-normal text-[#9aa0a6]">{invoice.billedTo?.email}</span></span></div>
        <div className="flex justify-between"><span className="text-[#5f6368] dark:text-[#9aa0a6]">Order ID</span><span className="font-mono text-xs text-[#3c4043] dark:text-[#dadce0]">{invoice.razorpayOrderId || '—'}</span></div>
        <div className="flex justify-between"><span className="text-[#5f6368] dark:text-[#9aa0a6]">Payment ID</span><span className="font-mono text-xs text-[#3c4043] dark:text-[#dadce0]">{invoice.razorpayPaymentId || '—'}</span></div>
      </div>
      <div className="flex justify-end gap-2 pt-5 mt-2 border-t border-[#e8eaed] dark:border-[#3c4043]">
        <button onClick={() => window.print()} className="px-4 py-2 rounded-full text-xs font-semibold text-[#5f6368] dark:text-[#9aa0a6] border border-[#dadce0] dark:border-[#3c4043] hover:bg-[#f1f3f4] dark:hover:bg-[#202124] cursor-pointer">Print</button>
        <button onClick={onClose} className="px-4 py-2 rounded-full text-xs font-semibold bg-[#1a73e8] hover:bg-[#1557b0] text-white cursor-pointer">Close</button>
      </div>
    </Modal>
  );
}

export default function PaymentsSubscriptionsPage() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);

  async function fetchStatus() {
    setLoading(true);
    try {
      setStatus(await getSubscriptionStatus());
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to load subscription status', 'error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleBuy() {
    setShowConfirm(false);
    setPaying(true);
    try {
      await loadRazorpayScript();
      const order = await createOrder();
      const rzp = new window.Razorpay({
        key: order.keyId,
        amount: order.amount,
        currency: order.currency,
        order_id: order.orderId,
        name: 'GymPortal Premium',
        description: 'Premium subscription',
        prefill: { name: user?.name, email: user?.email },
        theme: { color: '#1a73e8' },
        handler: async (response) => {
          try {
            await verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            showToast("Welcome to Premium! Your new features are unlocked.");
            fetchStatus();
          } catch {
            showToast('Payment verification failed', 'error');
          } finally {
            setPaying(false);
          }
        },
        modal: { ondismiss: () => setPaying(false) },
      });
      rzp.on('payment.failed', (response) => {
        reportPaymentFailed({
          razorpay_order_id: response.error?.metadata?.order_id,
          reason: response.error?.description,
        }).catch(() => {});
      });
      rzp.open();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to start checkout', 'error');
      setPaying(false);
    }
  }

  async function handleViewInvoice() {
    setInvoiceLoading(true);
    try {
      setInvoice(await getInvoice());
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to load invoice', 'error');
    } finally {
      setInvoiceLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-40">
        <div className="w-10 h-10 rounded-full border-4 border-[#e8f0fe] dark:border-[#1a2e4c] border-t-[#1a73e8] animate-spin" />
      </div>
    );
  }

  const isPremium = !!status?.isPremium;
  const trialActive = !!status?.trialActive;
  const trialDaysLeft = status?.trialEndsAt ? daysLeft(status.trialEndsAt) : 0;
  const amountLabel = status?.amount != null ? formatAmount(status.amount, status.currency) : '₹1,999';

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-[#202124] dark:text-[#e8eaed]">Payments & Subscription</h2>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Wallet} label="Plan" value={amountLabel} sublabel="per month" tone="green" />
        <StatCard icon={TrendingUp} label="Status" value={isPremium ? 'Premium' : trialActive ? 'Trial' : 'Expired'} tone={isPremium ? 'green' : trialActive ? 'purple' : 'red'} />
        {isPremium ? (
          <StatCard icon={Clock} label="Active Until" value={status.expiresAt ? new Date(status.expiresAt).toLocaleDateString('en-GB') : '—'} tone="blue" />
        ) : (
          <StatCard icon={Clock} label="Trial Ends In" value={trialActive ? `${trialDaysLeft}d` : '0d'} sublabel={status?.trialEndsAt ? new Date(status.trialEndsAt).toLocaleDateString('en-GB') : undefined} tone="purple" />
        )}
        <StatCard icon={Receipt} label="Last Purchase" value={status?.purchasedAt ? new Date(status.purchasedAt).toLocaleDateString('en-GB') : '—'} tone="amber" />
      </div>

      <div className={`google-card relative overflow-hidden p-6 sm:p-7 border ${
        isPremium
          ? 'bg-gradient-to-br from-[#e6f4ea] to-[#d7f0e0] dark:from-[#0d2818] dark:to-[#0a1f13] border-green-200 dark:border-green-900'
          : trialActive
            ? 'bg-gradient-to-br from-[#f3e8fd] to-[#ecdbfb] dark:from-[#241a33] dark:to-[#1a1326] border-purple-200 dark:border-purple-900'
            : 'bg-gradient-to-br from-[#fce8e6] to-[#fbd9d6] dark:from-[#2a1512] dark:to-[#1f0f0d] border-red-200 dark:border-red-900'
      }`}>
        <div className="absolute -right-10 -top-16 w-56 h-56 rounded-full bg-white/30 dark:bg-white/5 blur-3xl pointer-events-none" />
        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide ${
              isPremium ? 'bg-[#1e8e3e] text-white' : trialActive ? 'bg-[#9334e6] text-white' : 'bg-[#d93025] text-white'
            }`}>
              {isPremium ? <CheckCircle2 className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />}
              {isPremium ? 'Premium Active' : trialActive ? 'Free Trial' : 'Trial Expired'}
            </span>
            <h3 className="text-xl font-bold text-[#202124] dark:text-[#e8eaed] mt-2">
              {isPremium ? "You're all set on Premium." : trialActive ? `You have ${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'} left on your free trial.` : 'Your free trial has ended.'}
            </h3>
            <p className="text-sm text-[#3c4043] dark:text-[#dadce0] mt-1">
              {isPremium
                ? status.expiresAt ? `Your subscription is active until ${new Date(status.expiresAt).toLocaleDateString('en-GB')}.` : 'Your subscription is active.'
                : trialActive
                  ? "You won't be charged automatically — billing only starts once you choose to upgrade to Premium."
                  : 'Upgrade to Premium to keep using premium features.'}
            </p>
            {isPremium && (
              <button onClick={handleViewInvoice} disabled={invoiceLoading}
                className="mt-2 text-xs font-semibold text-[#1e8e3e] dark:text-green-300 hover:underline disabled:opacity-60 cursor-pointer">
                {invoiceLoading ? 'Loading invoice...' : 'View Invoice →'}
              </button>
            )}
          </div>
          {!isPremium && (
            <button onClick={() => setShowConfirm(true)} disabled={paying}
              className="px-5 py-2.5 rounded-full text-sm font-semibold bg-[#9334e6] hover:bg-[#7c2bc4] text-white shadow-xs cursor-pointer shrink-0 flex items-center gap-1.5 disabled:opacity-60">
              <Crown className="w-4 h-4" /> {paying ? 'Processing...' : 'Upgrade to Premium'}
            </button>
          )}
        </div>
      </div>

      <div className="google-card p-6 bg-gradient-to-br from-[#202124] to-[#3c4043] text-white overflow-hidden relative max-w-md">
        <div className="absolute -right-8 -top-10 w-40 h-40 rounded-full bg-white/5 blur-2xl pointer-events-none" />
        <div className="relative">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide bg-white/10 text-white/80">
            <Crown className="w-3 h-3" /> Premium
          </span>
          <div className="mt-3 flex items-baseline gap-1">
            <span className="text-3xl font-bold">{amountLabel}</span>
            <span className="text-sm text-white/60">/month</span>
          </div>
          <p className="text-xs text-white/60 mt-1">Billed monthly · cancel anytime</p>
          <p className="text-xs text-white/50 mt-3 leading-relaxed">Premium membership supports the platform and unlocks owner tools as they ship.</p>

          {isPremium ? (
            <span className="mt-5 w-full py-2.5 rounded-full text-sm font-semibold bg-white/10 text-white flex items-center justify-center gap-1.5">
              <CheckCircle2 className="w-4 h-4" /> Currently Active
            </span>
          ) : (
            <button onClick={() => setShowConfirm(true)} disabled={paying}
              className="mt-5 w-full py-2.5 rounded-full text-sm font-semibold bg-white text-[#202124] hover:bg-white/90 cursor-pointer disabled:opacity-60">
              {paying ? 'Processing...' : status?.purchasedAt ? 'Renew Premium' : 'Buy Premium'}
            </button>
          )}
        </div>
      </div>

      {showConfirm && (
        <Modal title="Upgrade to Premium?" icon={Sparkles} onClose={() => setShowConfirm(false)}>
          <p className="text-sm text-[#3c4043] dark:text-[#dadce0]">
            You&apos;re about to purchase the Premium plan ({amountLabel}/month) via Razorpay.
          </p>
          <div className="pt-4 flex justify-end gap-2">
            <button onClick={() => setShowConfirm(false)} className="px-4 py-2 rounded-full text-xs font-semibold text-[#5f6368] hover:bg-[#f1f3f4] dark:hover:bg-[#202124] cursor-pointer">
              Cancel
            </button>
            <button onClick={handleBuy} className="px-5 py-2 rounded-full text-xs font-semibold bg-[#34A853] hover:bg-[#2d8e47] text-white cursor-pointer">
              Continue to Payment
            </button>
          </div>
        </Modal>
      )}

      {invoice && <InvoiceModal invoice={invoice} onClose={() => setInvoice(null)} />}
    </div>
  );
}
